import React, {
  MouseEventHandler,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Popover, PopoverProps } from '@mui/material';
import InfoGrayIcon from 'spw-static/images/info-gray.svg';
import { currentUser as currentUserSelector } from 'spw-redux/modules/user/selectors';
import { IUser } from 'spw-redux/modules/user/types';
import { SPWIconButton } from 'spw-shared/globalComponents';
import Img from 'react-cool-img';
import { Page } from './types';

export interface InfoItemVM {
  title: string | JSX.Element;
  content: string | ReactNode;
}

enum TeamsListInfoPopoverModes {
  OrgAdminViewingTeamsTab = 'OrgAdminViewingTeamsTab',
  OrgAdminViewingRequestsTab = 'OrgAdminViewingRequestsTab',
  UserViewingTeamsTab = 'UserViewingTeamsTab',
  UserViewingRequestsTab = 'UserViewingRequestsTab',
}

enum UserDetailInfoPopoverModes {
  AdmViewingAnothersTeamsTab = 'AdmViewingAnothersTeamsTab',
  AdmViewingSelfTeamsTab = 'AdmViewingSelfTeamsTab',
  AdmViewingSelfProjectsOrClonedProjectsTab = 'AdmViewingSelfProjectsOrClonedProjectsTab',
  UserViewingAnothersTeamsTab = 'UserViewingAnothersTeamsTab',
  UserViewingAnothersProjectsOrClonedProjectsTab = 'UserViewingAnothersProjectsOrClonedProjectsTab',
}

const CLIENT_DETAIL_INFO_LIST: InfoItemVM[] = [
  {
    title: 'CLONE A PROJECT',
    content:
      'Cloning a project makes an editable copy which is not linked to the original. Cloned copies can be turned into active projects that are billable.',
  },
];

const TeamsListInfoLists: Record<TeamsListInfoPopoverModes, InfoItemVM[]> = {
  [TeamsListInfoPopoverModes.OrgAdminViewingTeamsTab]: [
    {
      title: 'JOIN A TEAM',
      content: 'Teams set to JOIN are open to all users in the organization. ',
    },
    {
      title: 'ASK TO JOIN A TEAM',
      content:
        'Teams set to ASK TO JOIN require approval before a user can join the team. ',
    },
    {
      title: 'LEAVE A TEAM',
      content:
        'Leaving a team removes edit access to all team projects. You will keep ownership of the projects you created when you leave.',
    },
    {
      title: 'REASSIGN TEAM ADMIN',
      content:
        'You are the only Team Admin. Another Team Admin must be assigned before you can leave the team.',
    },
  ],
  [TeamsListInfoPopoverModes.OrgAdminViewingRequestsTab]: [
    {
      title: 'APPROVE REQUEST TO JOIN THE TEAM',
      content:
        'Users become Team Members and have edit access to all team projects when a request is approved.',
    },
    {
      title: 'DENY REQUEST TO JOIN THE TEAM',
      content:
        'Users will not become a Team Member and can only view team projects when a request is denied.',
    },
  ],
  [TeamsListInfoPopoverModes.UserViewingTeamsTab]: [],
  [TeamsListInfoPopoverModes.UserViewingRequestsTab]: [],
};

const UserDetailInfoLists: Record<UserDetailInfoPopoverModes, InfoItemVM[]> = {
  [UserDetailInfoPopoverModes.AdmViewingAnothersTeamsTab]: [
    {
      title: 'REMOVE USER FROM THE TEAM',
      content:
        'Removing a user from the team removes their edit access to all team projects. The user will keep ownership of the projects they created.',
    },
    {
      title: 'REASSIGN TEAM ADMIN',
      content:
        'The user is the only Team Admin. Another Team Admin must be assigned before the user can be removed from the team.',
    },
  ],
  [UserDetailInfoPopoverModes.AdmViewingSelfTeamsTab]: [
    {
      title: 'LEAVE A TEAM',
      content:
        'Leaving a team removes edit access to all team projects. You will keep ownership of the projects you created when you leave.',
    },
    {
      title: 'REASSIGN TEAM ADMIN',
      content:
        'Assign another Team Admin to the team before the user can be removed from the team.',
    },
  ],
  [UserDetailInfoPopoverModes.AdmViewingSelfProjectsOrClonedProjectsTab]: [
    {
      title: 'LEAVE A PROJECT',
      content: 'You lose edit access to projects that you leave.',
    },
    {
      title: 'TRANSFER PROJECT OWNERSHIP',
      content: 'Transfer the project to anyone in the organization.',
    },
  ],
  [UserDetailInfoPopoverModes.UserViewingAnothersTeamsTab]: [
    {
      title: 'REMOVE TEAM FROM THE PROJECT',
      content:
        'All Team Members of the team lose edit access when the team is removed from a project.',
    },
  ],
  [UserDetailInfoPopoverModes.UserViewingAnothersProjectsOrClonedProjectsTab]: [
    {
      title: 'REMOVE USER FROM THE PROJECT',
      content:
        'The user loses edit access when they are removed from a project.',
    },
  ],
};

const getTeamsListInfoList = (
  currentUser: IUser,
  tab: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  viewedUser: Record<string, unknown>,
  isAdmin: boolean
) => {
  let key: TeamsListInfoPopoverModes;

  if (isAdmin) {
    key =
      tab === 'projects'
        ? TeamsListInfoPopoverModes.OrgAdminViewingTeamsTab
        : TeamsListInfoPopoverModes.OrgAdminViewingRequestsTab;
  } else {
    key =
      tab === 'projects'
        ? TeamsListInfoPopoverModes.UserViewingTeamsTab
        : TeamsListInfoPopoverModes.UserViewingRequestsTab;
  }

  return TeamsListInfoLists[key];
};

const getUserDetailInfoList = (
  currentUser: IUser,
  tab: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  viewedUser: Record<string, unknown>,
  isAdmin: boolean
) => {
  const userViewingTheirOwnProfile = true; // TODO: implement when having real data
  let key: UserDetailInfoPopoverModes;

  if (!isAdmin) {
    key =
      tab === 'teams'
        ? UserDetailInfoPopoverModes.UserViewingAnothersTeamsTab
        : UserDetailInfoPopoverModes.UserViewingAnothersProjectsOrClonedProjectsTab;
    return UserDetailInfoLists[key];
  }

  if (tab === 'teams') {
    key = userViewingTheirOwnProfile
      ? UserDetailInfoPopoverModes.AdmViewingSelfTeamsTab
      : UserDetailInfoPopoverModes.AdmViewingAnothersTeamsTab;
  } else {
    key = UserDetailInfoPopoverModes.AdmViewingSelfProjectsOrClonedProjectsTab;
  }

  return UserDetailInfoLists[key];
};

// TODO: will have to extend this logic to support teams list page
const getInfoList = (
  currentUser: IUser,
  page: Page,
  tab: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  viewedUser: Record<string, unknown>
) => {
  const isAdmin = true; // TODO: implement when having the final and real set of roles implemented

  switch (page) {
    case 'teamsList':
      return getTeamsListInfoList(currentUser, tab, viewedUser, isAdmin);

    case 'userDetail':
      return getUserDetailInfoList(currentUser, tab, viewedUser, isAdmin);

    default:
      return CLIENT_DETAIL_INFO_LIST;
  }
};

const InfoItem = styled.div`
  .title {
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 8px;

    & > div {
      display: flex;
      align-items: center;

      & img {
        margin-right: 8px;
      }
    }
  }
  .content {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
  }
  margin-bottom: 16px;
  :last-child {
    margin-bottom: 0px;
  }
`;
const PopoverContainer = styled.div`
  padding: 16px;
  box-sizing: border-box;
  width: 332px;
`;

interface Props {
  anchorOrigin?: PopoverProps['anchorOrigin'];
  page?: Page;
  tab?: string;
  transformOrigin?: PopoverProps['transformOrigin'];
  providedInfoList?: InfoItemVM[];
  popoverStyles?: React.CSSProperties;
}

const DEFAULT_ANCHOR_ORIGIN = {
  vertical: 'bottom',
  horizontal: -10,
} as PopoverProps['anchorOrigin'];

const DEFAULT_TRANSFORM_ORIGIN = {
  vertical: 'bottom',
  horizontal: 'right',
} as PopoverProps['transformOrigin'];

function InfoButton({
  anchorOrigin,
  page,
  tab,
  transformOrigin = DEFAULT_TRANSFORM_ORIGIN,
  providedInfoList,
  popoverStyles = {},
}: Props) {
  const [anchorEl, setAnchorEl] = useState<Element>();
  const currentUser = useSelector(currentUserSelector);

  const infoList = useMemo(() => getInfoList(currentUser, page, tab, {}), [
    currentUser,
    page,
    tab,
  ]);

  const handleInfoIconClick = useCallback<MouseEventHandler>(
    ({ target }): void => setAnchorEl(target as Element),
    [setAnchorEl]
  );

  return (
    <>
      {((providedInfoList && providedInfoList.length > 0) ||
        (!providedInfoList && infoList.length > 0)) && (
        <SPWIconButton onClick={handleInfoIconClick}>
          <Img src={InfoGrayIcon} alt="more info" />
        </SPWIconButton>
      )}
      <Popover
        disableScrollLock
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(undefined)}
        anchorOrigin={anchorOrigin ?? DEFAULT_ANCHOR_ORIGIN}
        transformOrigin={anchorOrigin ? undefined : transformOrigin}
      >
        <PopoverContainer style={popoverStyles}>
          {(providedInfoList || infoList).map((info) => (
            <InfoItem key={info.title?.toString()}>
              <div className="title">{info.title}</div>
              <div className="content">{info.content}</div>
            </InfoItem>
          ))}
        </PopoverContainer>
      </Popover>
    </>
  );
}

export default InfoButton;
